import MoreVert from '@mui/icons-material/MoreVert'
import { Box, Button, Container, Grid, IconButton, Menu, MenuItem, Typography } from '@mui/material'
import { ColumnDef, TableState } from '@tanstack/react-table'
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import {
  assignHighlight,
  deleteBrand,
  getBrands,
  orderedBrands,
  showBrand,
  toggleFormBrands,
  viewBrand
} from '../../../../../redux/actions/brands'
import { getFamilies } from '../../../../../redux/actions/families'
import { IRootState } from '../../../../../redux/types'
import CustomSnackBar from '../../../../shared/components/CustomSnackBar'
import Header from '../../../../shared/components/Header'
import ScreenLoading from '../../../../shared/components/ScreenLoading'
import HighlightIcon from '../../../../shared/components/icons/HighlightIcon'
import ReactTable from '../../../../shared/components/table/ReactTable'
import useScreenWidth from '../../../../shared/hooks/useScreenWidth'
import SubHeader from '../../../../shared/styles/Subheader'
import BrandsModal from '../components/BrandModal'
import DeleteBrandModal from '../components/RemoveBrandModal'

type IFamily = {
  id: number
  name: string
  code: string
}
export type IBrands = {
  id: number
  name: string
  description: string
  background: string
  main_color: string
  secondary_color: string
  highlight: number
  light_logo: string
  dark_logo: string
  familiesIds: number[]
  families: IFamily[]
  order: number
}
type BrandsOrder = {
  id: number
  order: number
}

type MenuOptions = {
  open: boolean
  id?: number
}

function Brands() {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user: authUser } = useSelector((state: IRootState) => state.auth)
  const { brands, showForm, error, msg, loading } = useSelector((state: IRootState) => state.brand)
  const [anchorOp, setAnchorOp] = useState<null | HTMLElement>(null)
  const [openOptions, setOpenOptions] = useState<MenuOptions>({ open: false })
  const [openDeleteBrand, setOpenDeleteBrand] = useState(false)
  const [brandSelected, setBrandSelected] = useState<number>()
  const [newBrandsOrder, setNewBrandsOrder] = useState<BrandsOrder[]>()
  const [isOrderModified, setIsOrderModified] = useState<boolean>(false)

  useEffect(() => {
    dispatch(getBrands())
    dispatch(getFamilies())
  }, [])

  const handleClickHighlight = useCallback((id: number, isHighlight: boolean) => {
    dispatch(
      assignHighlight({
        id,
        isHighlight
      })
    )
  }, [])

  const handleCloseOptions = () => {
    setAnchorOp(null)
    setOpenOptions({ open: false })
  }

  const getOrderedBrands = (data: IBrands[]) => {
    const orderedBrandsById = data.map((brand, index) => {
      return {
        id: brand.id,
        order: index + 1
      }
    })
    setNewBrandsOrder(orderedBrandsById)
    setIsOrderModified(true)
  }

  const tableState: Partial<TableState> = {
    columnVisibility: isMobile ? { color: false } : {}
  }

  const defaultColumns: ColumnDef<IBrands>[] = useMemo<ColumnDef<IBrands>[]>(
    () => [
      {
        header: t('name'),
        accessorKey: 'name',
        cell: rowData => (
          <Box display="flex" alignItems="center" gap={8}>
            <Typography
              sx={theme => ({
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                maxWidth: 300,
                fontWeight: 300,
                [theme.breakpoints.down('sm')]: {
                  maxWidth: 150
                }
              })}
            >
              {rowData.row.original.name}
            </Typography>
            <HighlightIcon id={rowData.row.original.id} isHighlight={!!rowData.row.original.highlight} handleClick={handleClickHighlight} />
          </Box>
        )
      },
      {
        header: t('family'),
        id: 'families',
        accessorFn: row => row.families,
        cell: (rowData: any) =>
          rowData.getValue().length > 0 ? (
            <span>
              {rowData
                .getValue()
                .map((family: any) => {
                  return t(`${family.code}`)
                })
                .join(', ')}
            </span>
          ) : (
            <></>
          )
      },
      {
        header: t('color'),
        id: 'color',
        cell: rowData => (
          <Box
            style={{
              backgroundColor: `${rowData.row.original.main_color}`,
              borderRadius: '4px',
              height: '20px',
              width: '48px'
            }}
          />
        )
      },
      { header: t('description'), accessorKey: 'description' },
      {
        header: '',
        id: 'options',
        cell: rowData => (
          <>
            <IconButton
              aria-controls="simple-menu"
              aria-haspopup="false"
              aria-label="more"
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                setAnchorOp(event.currentTarget)
                setOpenOptions({ open: true, id: rowData.row.original.id })
              }}
            >
              <MoreVert />
            </IconButton>
          </>
        )
      }
    ],
    [handleClickHighlight, t]
  )

  return (
    <>
      {showForm && <BrandsModal />}
      <Header title={t('brands')} />
      <SubHeader />
      {authUser !== null && (
        <>
          <Container>
            <Box data-testid="client-main-container" style={{ display: showForm ? 'none' : 'block' }} mb={6}>
              <Container maxWidth="lg">
                <Box mt={isMobile ? 2 : 6}>
                  <Grid container alignItems="center" spacing={3}>
                    <Grid item xs={12} md={2}>
                      <Button
                        data-testid="btn-create-brand"
                        variant="outlined"
                        color="secondary"
                        onClick={() => dispatch(toggleFormBrands())}
                      >
                        {t('create-brand')}
                      </Button>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Typography variant="h5" align="center">
                        {t('brands')} | {t('all')}
                      </Typography>
                    </Grid>
                    {/*<Grid item xs md={2}></Grid>*/}
                  </Grid>
                </Box>
                <Box mt={3}>
                  <ReactTable
                    key="brand-react-table"
                    data={brands}
                    data-testid="brand-react-table"
                    columns={defaultColumns}
                    state={tableState}
                    draggableRow
                    getDataOrdered={getOrderedBrands}
                  />
                </Box>
                {isOrderModified && (
                  <Button
                    id="btn-save-order"
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setIsOrderModified(false)
                      dispatch(orderedBrands(newBrandsOrder))
                    }}
                  >
                    {t('save-order')}
                  </Button>
                )}
              </Container>
            </Box>
          </Container>
          <DeleteBrandModal
            open={openDeleteBrand}
            onClose={() => setOpenDeleteBrand(false)}
            onConfirm={() => {
              dispatch(deleteBrand(brandSelected))
              setOpenDeleteBrand(false)
            }}
            isMobile={isMobile}
          />
        </>
      )}

      <Menu
        id={`simple-menu-${openOptions.id}`}
        anchorEl={anchorOp}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        open={openOptions.open}
        data-testid="MoreOptionsMenu"
        onClick={handleCloseOptions}
        onClose={handleCloseOptions}
      >
        <MenuItem
          data-testid="viewOption"
          onClick={() => {
            dispatch(viewBrand(openOptions.id))
            navigate('/admin/marcasView')
          }}
        >
          {t('view')}
        </MenuItem>
        <MenuItem data-testid="editOption" onClick={() => dispatch(showBrand(openOptions.id))}>
          {t('edit')}
        </MenuItem>
        <MenuItem
          data-testid="deleteOption"
          onClick={() => {
            setOpenDeleteBrand(!openDeleteBrand)
            setBrandSelected(openOptions.id)
          }}
        >
          {t('delete')}
        </MenuItem>
      </Menu>

      <CustomSnackBar errors={[error]} msgs={[msg]} />
      <ScreenLoading loadings={[loading]} />
    </>
  )
}

export default Brands
